import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MUNDIAL_WELCOME_ENABLED, peekMundialWelcomePending } from '../config/mundialWelcome';
import '../styles/argentina-mundial-welcome-premium.css';

const CONFETTI = Array.from({ length: 28 }, (_, index) => ({
  id: index,
  left: `${4 + ((index * 17) % 92)}%`,
  delay: `${(index % 7) * 0.11}s`,
  duration: `${2.8 + (index % 5) * 0.35}s`,
  tone: index % 3
}));

export default function ArgentinaMundialWelcome({ open, onClose, displayName = '' }) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => setEntered(true));
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!MUNDIAL_WELCOME_ENABLED || !open || typeof document === 'undefined') return null;

  const greeting = displayName ? `¡Bienvenido, ${displayName}!` : '¡Bienvenido al CRM!';

  return createPortal(
    <div
      className={`crm-mundial-welcome${entered ? ' is-entered' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="crm-mundial-welcome-title"
    >
      <button
        type="button"
        className="crm-mundial-welcome__backdrop"
        aria-label="Cerrar celebración del Mundial"
        onClick={onClose}
      />
      <div className="crm-mundial-welcome__confetti" aria-hidden="true">
        {CONFETTI.map((piece) => (
          <span
            key={piece.id}
            className={`crm-mundial-welcome__confetti-piece crm-mundial-welcome__confetti-piece--${piece.tone}`}
            style={{
              left: piece.left,
              animationDelay: piece.delay,
              animationDuration: piece.duration
            }}
          />
        ))}
      </div>
      <div className="crm-mundial-welcome__card">
        <div className="crm-mundial-welcome__stripes" aria-hidden="true">
          <span /><span /><span /><span /><span />
        </div>
        <div className="crm-mundial-welcome__glow" aria-hidden="true" />
        <button
          type="button"
          className="crm-mundial-welcome__close"
          onClick={onClose}
          aria-label="Cerrar"
        >
          ×
        </button>
        <div className="crm-mundial-welcome__hero">
          <div className="crm-mundial-welcome__crest" aria-hidden="true">
            <svg viewBox="0 0 120 120" className="crm-mundial-welcome__sun">
              <circle cx="60" cy="60" r="54" fill="url(#mundialSun)" />
              <g stroke="#F4B740" strokeWidth="3.2" strokeLinecap="round">
                {Array.from({ length: 16 }).map((_, i) => {
                  const angle = (i * Math.PI) / 8;
                  const x1 = 60 + Math.cos(angle) * 18;
                  const y1 = 60 + Math.sin(angle) * 18;
                  const x2 = 60 + Math.cos(angle) * 34;
                  const y2 = 60 + Math.sin(angle) * 34;
                  return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
                })}
              </g>
              <circle cx="60" cy="60" r="12" fill="#F4B740" />
              <defs>
                <radialGradient id="mundialSun" cx="50%" cy="45%" r="60%">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="55%" stopColor="#cfe9fb" />
                  <stop offset="100%" stopColor="#74acdf" />
                </radialGradient>
              </defs>
            </svg>
            <span className="crm-mundial-welcome__stars" aria-hidden="true">
              <span>★</span>
              <span>★</span>
              <span>★</span>
            </span>
          </div>
          <p className="crm-mundial-welcome__eyebrow">Mundial 2026 · Grupo Bienestar</p>
          <h2 id="crm-mundial-welcome-title" className="crm-mundial-welcome__title">
            <span className="crm-mundial-welcome__title-flag" aria-hidden="true">
              🇦🇷
            </span>
            Argentina está en el Mundial
          </h2>
          <p className="crm-mundial-welcome__subtitle">
            La Scaloneta vuelve a competir por la gloria. Entrá al CRM con la energía de campeones del
            mundo.
          </p>
        </div>
        <div className="crm-mundial-welcome__stats">
          <div className="crm-mundial-welcome__stat">
            <strong>3</strong>
            <span>Estrellas</span>
          </div>
          <div className="crm-mundial-welcome__stat">
            <strong>2026</strong>
            <span>Copa del Mundo</span>
          </div>
          <div className="crm-mundial-welcome__stat">
            <strong>USA</strong>
            <span>México · Canadá</span>
          </div>
        </div>
        <p className="crm-mundial-welcome__greeting">{greeting}</p>
        <button type="button" className="crm-mundial-welcome__cta" onClick={onClose}>
          <span className="crm-mundial-welcome__cta-shine" aria-hidden="true" />
          <span>¡Vamos Argentina!</span>
        </button>
      </div>
    </div>,
    document.body
  );
}

export function useArgentinaMundialWelcome(user) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!MUNDIAL_WELCOME_ENABLED || !user) {
      setOpen(false);
      return;
    }
    if (peekMundialWelcomePending()) {
      const timer = window.setTimeout(() => setOpen(true), 420);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [user]);

  return {
    open,
    close: () => setOpen(false),
    displayName: String(user?.name || user?.username || '').trim()
  };
}
